import { Calendar as CalendarIcon } from "lucide-react";
import React from "react";
import type { DateRange } from "react-day-picker";
import { ru } from "react-day-picker/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Helpers to convert between "YYYY-MM-DD" strings (the filter format the backend
// expects) and Date objects, always using LOCAL date parts so the picked day is
// never shifted by the timezone (as toISOString would do).
export function parseLocalDate(s: string): Date | undefined {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

export function formatLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDisplayDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

interface DateRangeFilterProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
  /** Текст кнопки при пустом фильтре (дефолт «Период»). */
  placeholder?: string;
  /** Тултип кнопки. */
  title?: string;
  /** Компактный вид: h-8, text-xs, мелкая иконка (по умолчанию h-9). */
  size?: "default" | "sm";
  /** Клик по готовому диапазону начинает новый: первый клик — начало, второй — конец. Без этого
   *  react-day-picker двигает конец (начало — только кликом левее него). */
  resetOnSelect?: boolean;
}

/** Общий фильтр-диапазон дат (Popover + календарь, ru-локаль). Извлечён из reviews. */
export function DateRangeFilter({
  dateFrom,
  dateTo,
  onChange,
  placeholder = "Период",
  title,
  size = "default",
  resetOnSelect = false,
}: DateRangeFilterProps): React.ReactElement {
  const selected: DateRange | undefined = (() => {
    const from = parseLocalDate(dateFrom);
    const to = parseLocalDate(dateTo);
    if (!from && !to) return undefined;
    return { from, to };
  })();

  const label = (() => {
    const from = parseLocalDate(dateFrom);
    const to = parseLocalDate(dateTo);
    if (from && to) return `${formatDisplayDate(from)} — ${formatDisplayDate(to)}`;
    if (from) return `с ${formatDisplayDate(from)}`;
    if (to) return `по ${formatDisplayDate(to)}`;
    return placeholder;
  })();

  const hasValue = !!dateFrom || !!dateTo;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${size === "sm" ? "h-8 gap-1.5 px-3 text-xs" : "h-9 gap-2"} justify-start font-normal ${hasValue ? "" : "text-muted-foreground"}`}
          title={title}
        >
          <CalendarIcon className={size === "sm" ? "size-3.5" : "h-4 w-4"} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          locale={ru}
          numberOfMonths={2}
          defaultMonth={selected?.from}
          selected={selected}
          resetOnSelect={resetOnSelect}
          onSelect={(range) =>
            onChange(
              range?.from ? formatLocalDate(range.from) : "",
              range?.to ? formatLocalDate(range.to) : "",
            )
          }
        />
        <div className="flex items-center justify-end gap-3 border-t p-2">
          {resetOnSelect && (
            <span className="mr-auto pl-1 text-xs text-muted-foreground">
              {dateFrom && !dateTo
                ? "Выберите конец периода"
                : "Первый клик — начало периода, второй — конец"}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasValue}
            onClick={() => onChange("", "")}
          >
            Сбросить
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
